<!-- 
Home page. 
Shows a big clickable summary panel for the two most recent levy collections,
buttons for creating new levy collections or sandboxes, and
(for administrators) connectivity and error summary panels.
-->
<template>
  <div class="home">
    <our-heading 
      :breadcrumbs="breadcrumbs"
      title="Home"
      user-guide-topic="home"
    />
    <our-loading-indicator v-if="!loaded" />
    <our-actions-bar 
      v-if="loaded" 
      :actions="actions" 
      xs-breakpoint
      @create="showCreateDialog = true" 
      @sandbox="showSandboxesDialog = true"
    />
    <v-scale-transition>
      <v-container v-if="loaded" fluid grid-list-sm pa-0>
        <v-layout wrap>
          <v-flex 
            v-for="item in levyCollections" :key="item.id"
            xs12 sm6 xl4
          >
            <v-hover>
              <v-card 
                slot-scope="{ hover }"
                :to="`/levy-collections/${item.id}/dashboard`"
                class="home"
                ripple
                style="cursor:pointer;"
              >
                <div v-if="hover" class="d-flex reveal"/>
                <div style="overflow-y: auto; height: 297px;">
                  <v-card-text>
                    <div class="text-xs-center">
                      <img 
                        v-if="item.type == 'Vsl'" src="@/assets/vsl-logo.png" alt="VET Student Loans"
                        width="100%" class="pt-3" style="max-width: 300px"
                      >
                      <img 
                        v-if="item.type == 'Help'" src="@/assets/help-logo.png" alt="Higher Education"
                        width="100%" class="pt-3" style="max-width: 300px"
                      >
                      <img 
                        v-if="item.type == 'Upfront'" src="@/assets/upfront-logo.png" alt="Up-front"
                        width="100%" class="pt-3" style="max-width: 300px"
                      >
                    </div>
                    <div class="ml-1">
                      <p class="mb-2 mt-3">
                        <span class="grey--text text--darken-1 caption">Year</span><br>
                        <span class="font-weight-medium">{{ item.year }}</span><br>
                      </p>
                      <p class="mb-2">
                        <span class="grey--text text--darken-1 caption">Status</span><br>
                        <span class="font-weight-medium">{{ lookups.levyCollectionStatusDescriptions[item.status] }}</span><br>
                      </p>
                      <p v-if="item.inScopeProviders == 0" class="mb-0">
                        <span class="grey--text text-darken-1">No in-scope providers added</span>
                      </p>
                      <p v-else class="mb-0">
                        <span class="font-weight-medium">{{ item.inScopeProviders | number }} </span>
                        <span>{{ pluralise(item.inScopeProviders) }}</span>
                      </p>
                      <p class="mb-0">
                        <span class="font-weight-medium">{{ item.totalLevy | currency }} </span>
                        <span>total levy</span>
                      </p>
                    </div>
                  </v-card-text>
                </div>
                <v-card-actions>
                  <v-spacer/>
                  <v-btn 
                    :class="`open-${item.type.toLowerCase()}-levy-collection-button`" 
                    flat 
                    color="primary"
                  >
                    Open levy collection
                  </v-btn>
              </v-card-actions></v-card>
            </v-hover>
          </v-flex>
        </v-layout>
      </v-container>
    </v-scale-transition>
    <v-scale-transition>
      <v-container v-if="loaded" fluid grid-list-sm pa-0>
        <v-layout wrap>
          <v-flex 
            v-if="canViewExceptionLogs" 
            xs12 sm6 xl4
          >
            <v-card class="home">
              <div style="overflow-y: hidden; height: 349px;">
                <v-card-text>
                  <v-toolbar dense flat color="white" class="pl-1 mb-1">
                    <v-toolbar-title>
                      System monitoring
                    </v-toolbar-title>
                    <v-spacer/>
                    <v-tooltip top>
                      <v-btn-toggle 
                        slot="activator" :value="weekActive ? 0 : 1" class="mb-2 mt-2 elevation-0"
                        style="border: 1px solid #f0f0f0;" mandatory 
                        @change="toggle"
                      >
                        <v-btn flat color="primary">
                          <v-icon>view_week</v-icon>
                        </v-btn>
                        <v-btn flat color="primary">
                          <v-icon>timeline</v-icon>
                        </v-btn>
                      </v-btn-toggle>
                      <span>Switch between errors over last week and exception counts by month charts.</span>
                    </v-tooltip>
                    <our-icon-button
                      :loading="loadingExceptions"
                      icon="refresh"
                      tooltip="Refresh system monitoring charts"
                      @click="refreshExceptions()"
                    />
                  </v-toolbar>
                  <errors-over-last-week-chart v-show="weekActive" ref="errorsOverLastWeekChart" no-toolbar />
                  <exception-counts-by-month-chart v-show="monthActive" ref="exceptionCountsByMonthChart" no-toolbar />
                </v-card-text>
              </div>
            </v-card>
          </v-flex>
          <v-flex 
            v-if="canViewExceptionLogs" 
            xs12 sm6 xl4
          >
            <v-hover>
              <v-card 
                class="home"
              >
                <div style="overflow-y: auto; height: 349px;">
                  <v-card-text>
                    <h3 class="title text-xs-center mb-4 pb-2 mt-3">
                      Connection status of external dependencies
                    </h3>
                    <connectivity-panel/>
                  </v-card-text>
                </div>
              </v-card>
            </v-hover>
          </v-flex>
        </v-layout>
      </v-container>
    </v-scale-transition>
    <create-levy-collection-dialog v-model="showCreateDialog"/>
    <sandboxes-dialog v-model="showSandboxesDialog"/>
  </div>
</template>

<script>
import _ from "underscore";
import { EXCEPTION_LOGS_VIEW } from "@/store/claim-types";
import { LIST } from "@/store/levy-collections/action-types";
import { isCurrentLevyCollection } from "@/store/levy-collections/helpers";
import lookupMixins from "@/mixins/lookup.mixins";
import filterMixins from "@/mixins/filter.mixins";
import * as mutation from "@/store/exception-logs/mutation-types";
import * as claim from "@/store/claim-types";
import ConnectivityPanel from "@/components/exception-logs/ConnectivityPanel.vue";
import CreateLevyCollectionDialog from "@/components/levy-collections/CreateLevyCollectionDialog.vue";
import SandboxesDialog from "@/components/sandboxes/SandboxesDialog.vue";
import ErrorsOverLastWeekChart from "@/components/exception-logs/ErrorsOverLastWeekChart.vue";
import ExceptionCountsByMonthChart from "@/components/exception-logs/ExceptionCountsByMonthChart.vue";

export default {
  name: "Home",
  components: {
    ConnectivityPanel,
    CreateLevyCollectionDialog,
    SandboxesDialog,
    ErrorsOverLastWeekChart,
    ExceptionCountsByMonthChart
  },
  mixins: [lookupMixins, filterMixins],
  data() {
    return {
      showCreateDialog: false,
      showSandboxesDialog: false
    };
  },
  computed: {
    actions() {
      const actions = [];
      if (this.canCreate) {
        actions.push({ text: "Start a new levy collection", emit: "create", icon: "add_circle_outline" });
      }
      if (this.canEditSandboxes) {
        actions.push({ text: "Levy collection sandboxes", emit: "sandbox", icon: "move_to_inbox" });
      }
      return actions;
    },
    canEditSandboxes() {
      return this.$store.getters.claims.includes(claim.LEVY_COLLECTION_SANDBOXES);
    },
    canCreate() {
      return this.$store.getters.claims.includes(claim.LEVY_COLLECTION_CREATE);
    },
    levyCollections() {
      if (this.loaded) {
        return _.sortBy(this.listing.data.filter(lc => isCurrentLevyCollection(lc.id)), t => t.type);
      } else {
        return [];
      }
    },
    listing() {
      return this.$store.state.levyCollections.listing;
    },
    loaded() {
      return this.lookupsLoaded && this.listing.loaded;
    },
    breadcrumbs() {
      return [{ text: "Home" }];
    },
    canViewExceptionLogs() {
      return this.$store.getters.claims.includes(EXCEPTION_LOGS_VIEW);
    },
    loadingExceptions() {
      return this.$store.state.exceptionLogs.week.loading || this.$store.state.exceptionLogs.month.loading;
    },
    weekActive() {
      return this.$store.state.exceptionLogs.week.active;
    },
    monthActive() {
      return this.$store.state.exceptionLogs.month.active;
    }
  },
  watch: {
    loaded() {
      this.reload();
    }
  },
  mounted() {
    this.reload();
  },
  methods: {
    reload() {
      if (!this.listing.loaded && !this.listing.loading) {
        this.$store.dispatch(LIST);
      }
    },
    pluralise(count) {
      return `${count > 1 ? "in-scope providers" : "in-scope provider"}`;
    },
    refreshExceptions() {
      this.$refs.errorsOverLastWeekChart.refresh();
      this.$refs.exceptionCountsByMonthChart.refresh();
    },
    toggle() {
      this.$store.commit(mutation.STATS_TOGGLE_ACTIVE);
    }
  }
};
</script>
